import { Card, CardContent, CardHeader, CardTitle } from "@bcc/components/ui/card";
import type { PurchaseOrder } from "@shared/types";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

type Granularity = "daily" | "weekly" | "monthly";

interface CrossBorderAnalyticsProps {
  readonly orders: PurchaseOrder[];
  readonly testId?: string;
}

const formatDateKey = (date: Date, granularity: Granularity): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  switch (granularity) {
    case "daily":
      return `${year}-${month}-${day}`;
    case "weekly": {
      const startOfWeek = new Date(date);
      // getDay() returns 0 for Sunday; treat Sunday as day 7 so Monday is always day 1.
      const dayOfWeek = date.getDay() === 0 ? 7 : date.getDay();
      startOfWeek.setDate(date.getDate() - dayOfWeek + 1);
      const wYear = startOfWeek.getFullYear();
      const wMonth = String(startOfWeek.getMonth() + 1).padStart(2, "0");
      const wDay = String(startOfWeek.getDate()).padStart(2, "0");
      return `${wYear}-W ${wMonth}/${wDay}`;
    }
    case "monthly":
      return `${year}-${month}`;
  }
};

export function CrossBorderAnalytics({ orders, testId }: CrossBorderAnalyticsProps) {
  const { t } = useTranslation();
  const [granularity, setGranularity] = useState<Granularity>("daily");

  const chartData = useMemo(() => {
    const buckets = new Map<string, { DE_TO_NL: number; NL_TO_DE: number }>();

    for (const order of orders) {
      const date = new Date(order.createdAt);
      const key = formatDateKey(date, granularity);
      const direction = order.crossBorderDirection;

      if (!buckets.has(key)) {
        buckets.set(key, { DE_TO_NL: 0, NL_TO_DE: 0 });
      }

      const bucket = buckets.get(key);
      if (bucket) {
        if (direction === "DE_TO_NL") bucket.DE_TO_NL++;
        else if (direction === "NL_TO_DE") bucket.NL_TO_DE++;
      }
    }

    return Array.from(buckets.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([period, counts]) => ({
        period,
        "DE → NL": counts.DE_TO_NL,
        "NL → DE": counts.NL_TO_DE,
      }));
  }, [orders, granularity]);

  return (
    <Card data-testid={testId}>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t("admin.crossBorder.analytics.title")}</CardTitle>
        <div className="flex gap-1">
          {(["daily", "weekly", "monthly"] as const).map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setGranularity(g)}
              className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                granularity === g ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-accent"
              }`}
              data-testid={testId ? `${testId}-${g}` : undefined}
            >
              {t(`admin.crossBorder.analytics.${g}`)}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <div className="flex h-48 items-center justify-center text-muted-foreground">{t("admin.crossBorder.analytics.noData")}</div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" fontSize={12} />
              <YAxis allowDecimals={false} fontSize={12} />
              <Tooltip />
              <Legend />
              <Bar dataKey="DE → NL" stackId="a" fill="#3b82f6" />
              <Bar dataKey="NL → DE" stackId="a" fill="#f97316" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
